import { BarChartOutlined } from '@ant-design/icons';
import { Button, Card, Slider, Space, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const SliderInput = (props) => <Slider tooltip={{ open: false }} {...props} />;

const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;

function toValue({ a = 1, b = 0 } = {}) {
  const min = -b / a;
  const max = (255 - b) / a;

  return [min, max];
}

function SaveButton({ value: _value, onSave, ...props }) {
  //JJ 7/30/25 - Call to the legacy setImageLevels mutation was removed. Leaving the rest of the dead code in place to avoid snowballing scope of SC-119751 here
  const handleClick = useCallback(() => {
    onSave();
  }, [onSave]);

  return <Button onClick={handleClick} {...props} />;
}

export function Levels({ onChange, defaultValue = {} }) {
  const { t } = useTranslation();
  const { id, customerId, a, b } = defaultValue;

  const [levels, setLevels] = useState({ a, b });
  const [show, setShow] = useState(false);

  const hasLevels = useMemo(() => a !== 1 || b !== 0, [a, b]);

  // Reset the levels control to be hidden
  useEffect(() => {
    if (id) {
      setShow(false);
    }
  }, [id]);

  const handleChange = useCallback(
    ([min, max]) => {
      const a = Number((255 / (max - min)).toFixed(4));
      const b = Math.round(-a * min);
      const levels = { a, b };

      setLevels(levels);
      onChange({ levels });
    },
    [onChange]
  );

  const toggleShow = useCallback(() => setShow((prev) => !prev), []);
  const handleReset = useCallback(() => {
    hasLevels ? handleChange(toValue({ a, b })) : handleChange([0, 255]);
  }, [handleChange, hasLevels, a, b]);

  const handleCancel = useCallback(() => {
    handleReset();
    setShow(false);
  }, [handleReset]);

  return show ? (
    <Card>
      <SliderInput
        style={{ margin: '0 0 .5rem 0' }}
        value={toValue(levels)}
        id="image-levels"
        max={255}
        min={0}
        onChange={handleChange}
        range
        step={1}
      />

      <SpaceBetween style={{ marginBottom: '1rem' }}>
        <span>{t('components.shadows', 'Shadows')}</span>
        <span>{t('components.highlights', 'Highlights')}</span>
      </SpaceBetween>

      <SpaceBetween>
        <Button size="small" onClick={handleReset}>
          {t('table.reset', 'Reset')}
        </Button>

        <Space>
          <SaveButton
            id="save-image-levels"
            size="small"
            onSave={toggleShow}
            type="primary"
            value={{ id, customerId, levels }}
          >
            {t('common.save', 'Save')}
          </SaveButton>

          <Button id="cancel-image-levels" size="small" onClick={handleCancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </Space>
      </SpaceBetween>
    </Card>
  ) : (
    <Tooltip title={t('components.adjustImageLevels', 'Adjust image levels')}>
      <Button type={hasLevels ? 'primary' : undefined} id="show-image-levels" onClick={toggleShow}>
        <BarChartOutlined />
      </Button>
    </Tooltip>
  );
}
